import { Helmet } from 'react-helmet-async';
import { useState, useEffect } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import apiService from '@/services/api';
import { API_CONFIG } from '@/config';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Lock,
  Shield,
  LogOut,
  Download,
  Trash2,
} from 'lucide-react';
import DashboardLayout from '@/components/layout/DashboardLayout';

const AccountSettingsPage = () => {
  const stripe = useStripe();
  const elements = useElements();
  const [referralCode, setReferralCode] = useState('');
  const [referralCredits, setReferralCredits] = useState<number | null>(null);
  const [accountStatus, setAccountStatus] = useState<any>(null);
  const [billingData, setBillingData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [totpSetup, setTotpSetup] = useState<{ qr_code: string; secret: string } | null>(null);
  const [totpCode, setTotpCode] = useState('');
  const [enabling2FA, setEnabling2FA] = useState(false);
  const [showCardForm, setShowCardForm] = useState(false);
  const [savingCard, setSavingCard] = useState(false);
  const [cardError, setCardError] = useState<string | null>(null);

  const [personalData, setPersonalData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    jobTitle: '',
  });

  const [companyData, setCompanyData] = useState({
    name: '',
    registration: '',
    vat: '',
    industry: '',
  });

  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      try {
        const response = await apiService.request(API_CONFIG.ENDPOINTS.ACCOUNT_PROFILE, 'GET');
        if (response.success && response.data) {
          const d = response.data;
          setPersonalData({
            firstName: d.first_name || d.firstName || '',
            lastName: d.last_name || d.lastName || '',
            email: d.email || '',
            phone: d.phone || d.contact_phone || '',
            jobTitle: d.job_title || d.role || '',
          });
          setCompanyData({
            name: d.company_name || d.company || '',
            registration: d.company_registration || '',
            vat: d.vat_number || '',
            industry: d.industry || '',
          });
          setAccountStatus({
            customerId: d.customer_id ?? 'N/A',
            status: d.account_status || 'ACTIVE',
            memberSince: d.created_at ? new Date(d.created_at).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A',
            totalOrders: d.total_orders || 0,
            totalSpend: d.total_spend ? `£${(d.total_spend / 100).toFixed(2)}` : '£0.00',
          });
        }
      } catch (error) {
        console.error('Failed to fetch account profile:', error);
        setMessage({ type: 'error', text: 'Failed to load profile data' });
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();

    const fetchReferral = async () => {
      try {
        let response = await apiService.request('/referrals/my-referrals', 'GET');
        if (response.success && !response.referralCode) {
          // No code generated yet — create one so the share button works immediately
          const genResponse = await apiService.request('/referrals/generate', 'POST');
          if (genResponse.success) {
            response = { ...response, referralCode: genResponse.referral_code };
          }
        }
        if (response.success) {
          setReferralCode(response.referralCode || '');
          const pence = response.summary?.total_commission_earned_pence;
          setReferralCredits(pence != null ? pence / 100 : 0);
        }
      } catch (error) {
        console.error('Failed to fetch referral info:', error);
        setReferralCode('UNAVAILABLE');
      }
    };
    fetchReferral();

    const fetchBilling = async () => {
      try {
        const response = await apiService.request('/cards', 'GET');
        if (response.success && Array.isArray(response.data) && response.data.length > 0) {
          const card = response.data[0];
          setBillingData({
            cardLast4: card.card_last_four,
            cardExpiry: `${String(card.expiry_month).padStart(2, '0')}/${card.expiry_year}`,
            cardBrand: card.card_brand,
          });
        }
      } catch (error) {
        console.error('Failed to fetch billing info:', error);
      }
    };
    fetchBilling();
  }, []);

  const handleSavePersonalData = async () => {
    setSaving(true);
    try {
      const response = await apiService.request(API_CONFIG.ENDPOINTS.ACCOUNT_PROFILE_UPDATE, 'PUT', {
        first_name: personalData.firstName,
        last_name: personalData.lastName,
        phone: personalData.phone,
        job_title: personalData.jobTitle,
      });
      if (response.success) {
        setMessage({ type: 'success', text: 'Personal information saved successfully' });
      } else {
        setMessage({ type: 'error', text: response.message || 'Failed to save changes' });
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Failed to save changes' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCompanyData = async () => {
    setSaving(true);
    try {
      const response = await apiService.request(API_CONFIG.ENDPOINTS.ACCOUNT_COMPANY_UPDATE, 'PUT', {
        company_name: companyData.name,
        company_registration: companyData.registration,
        vat_number: companyData.vat,
        industry: companyData.industry,
      });
      if (response.success) {
        setMessage({ type: 'success', text: 'Company details saved successfully' });
      } else {
        setMessage({ type: 'error', text: response.message || 'Failed to save changes' });
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Failed to save changes' });
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    const newPassword = prompt('Enter new password:');
    if (!newPassword) return;
    setSaving(true);
    try {
      const response = await apiService.request(API_CONFIG.ENDPOINTS.ACCOUNT_CHANGE_PASSWORD, 'POST', {
        new_password: newPassword,
      });
      if (response.success) {
        setMessage({ type: 'success', text: 'Password changed successfully' });
      } else {
        setMessage({ type: 'error', text: response.message || 'Failed to change password' });
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Failed to change password' });
    } finally {
      setSaving(false);
    }
  };

  const handleEnable2FA = async () => {
    setEnabling2FA(true);
    setMessage({ type: '', text: '' });
    try {
      const response = await apiService.request('/auth/2fa/setup', 'POST');
      if (response.success && response.data) {
        setTotpSetup({ qr_code: response.data.qr_code, secret: response.data.secret });
      } else {
        setMessage({ type: 'error', text: response.message || 'Failed to start 2FA setup' });
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Failed to start 2FA setup' });
    } finally {
      setEnabling2FA(false);
    }
  };

  const handleVerify2FA = async () => {
    if (!totpSetup || !totpCode) return;
    setSaving(true);
    try {
      const response = await apiService.request('/auth/2fa/verify', 'POST', {
        totp_secret: totpSetup.secret,
        totp_code: totpCode,
      });
      if (response.success) {
        setMessage({ type: 'success', text: 'Two-factor authentication enabled' });
        setTotpSetup(null);
        setTotpCode('');
      } else {
        setMessage({ type: 'error', text: response.message || 'Invalid code, please try again' });
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Invalid code, please try again' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCard = async (e: React.FormEvent) => {
    e.preventDefault();
    setCardError(null);

    if (!stripe || !elements) {
      setCardError('Payment form is still loading, please try again in a moment.');
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) return;

    setSavingCard(true);
    try {
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });

      if (error) {
        setCardError(error.message || 'Failed to process card details');
        return;
      }

      const response = await apiService.request('/payments/update-card', 'POST', {
        paymentMethodId: paymentMethod.id,
      });

      if (response.success && response.data) {
        setBillingData({
          cardLast4: response.data.last4,
          cardExpiry: `${String(response.data.exp_month).padStart(2, '0')}/${response.data.exp_year}`,
          cardBrand: response.data.brand,
        });
        setShowCardForm(false);
        cardElement.clear();
        setMessage({ type: 'success', text: 'Payment method saved' });
      } else {
        setCardError(response.message || 'Failed to save card');
      }
    } catch (error: any) {
      setCardError(error.message || 'Failed to save card');
    } finally {
      setSavingCard(false);
    }
  };

  const handleShareReferral = async () => {
    if (!referralCode) {
      setMessage({ type: 'error', text: 'Referral code not available' });
      return;
    }
    const referralUrl = `${window.location.origin}?ref=${referralCode}`;
    navigator.clipboard.writeText(referralUrl);
    setMessage({ type: 'success', text: 'Referral link copied to clipboard!' });
  };

  const handleRequestDataExport = async () => {
    if (!confirm('This will send a data export to your email. Continue?')) return;
    setSaving(true);
    try {
      const response = await apiService.request(API_CONFIG.ENDPOINTS.ACCOUNT_EXPORT_DATA, 'POST', {});
      if (response.success) {
        setMessage({ type: 'success', text: 'Data export requested - check your email' });
      } else {
        setMessage({ type: 'error', text: response.message || 'Failed to request export' });
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Failed to request export' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivateAccount = async () => {
    if (!confirm('This will deactivate your account and pause all services. Are you sure?')) return;
    setSaving(true);
    try {
      const response = await apiService.request(API_CONFIG.ENDPOINTS.ACCOUNT_DEACTIVATE, 'POST', {});
      if (response.success) {
        setMessage({ type: 'success', text: 'Account deactivated. Redirecting to home...' });
        setTimeout(() => window.location.href = '/', 2000);
      } else {
        setMessage({ type: 'error', text: response.message || 'Failed to deactivate account' });
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Failed to deactivate account' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <Helmet>
        <title>Account Settings | Virewire</title>
      </Helmet>

      <div className="space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Account Settings</h1>
          <p className="text-gray-600 mt-2">
            Manage your Virewire account details and preferences
          </p>
        </div>

        {/* Message Alert */}
        {message.text && (
          <div className={`p-4 rounded-lg ${
            message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' :
            message.type === 'error' ? 'bg-red-50 text-red-800 border border-red-200' :
            'bg-blue-50 text-blue-800 border border-blue-200'
          }`}>
            {message.text}
          </div>
        )}

        <div className="grid grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="col-span-2 space-y-8">
            {/* Personal Information */}
            <Card className="border-1">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  Personal Information
                </h2>
                <div className="space-y-6">
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="text-xs font-bold text-gray-600 uppercase mb-2 block">
                        First Name
                      </label>
                      <input
                        type="text"
                        value={personalData.firstName}
                        onChange={(e) =>
                          setPersonalData({ ...personalData, firstName: e.target.value })
                        }
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 focus:bg-white focus:ring-2 focus:ring-green-600 outline-none"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-bold text-gray-600 uppercase mb-2 block">
                        Last Name
                      </label>
                      <input
                        type="text"
                        value={personalData.lastName}
                        onChange={(e) =>
                          setPersonalData({ ...personalData, lastName: e.target.value })
                        }
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 focus:bg-white focus:ring-2 focus:ring-green-600 outline-none"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-bold text-gray-600 uppercase mb-2 block">
                      Email Address
                    </label>
                    <div className="flex gap-3">
                      <input
                        type="email"
                        value={personalData.email}
                        disabled
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600"
                      />
                      <button className="text-green-700 font-semibold text-sm hover:underline whitespace-nowrap" disabled>
                        (Read-only)
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="text-xs font-bold text-gray-600 uppercase mb-2 block">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        value={personalData.phone}
                        onChange={(e) =>
                          setPersonalData({ ...personalData, phone: e.target.value })
                        }
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-bold text-gray-600 uppercase mb-2 block">
                        Job Title
                      </label>
                      <input
                        type="text"
                        value={personalData.jobTitle}
                        onChange={(e) =>
                          setPersonalData({ ...personalData, jobTitle: e.target.value })
                        }
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
                      />
                    </div>
                  </div>

                  <Button 
                    onClick={handleSavePersonalData}
                    disabled={saving}
                    className="bg-green-700 hover:bg-green-800 text-white"
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Company Details */}
            <Card className="border-1">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  Company Details
                </h2>
                <div className="space-y-6">
                  <div>
                    <label className="text-xs font-bold text-gray-600 uppercase mb-2 block">
                      Company Name
                    </label>
                    <input
                      type="text"
                      value={companyData.name}
                      onChange={(e) =>
                        setCompanyData({ ...companyData, name: e.target.value })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="text-xs font-bold text-gray-600 uppercase mb-2 block">
                        Registration Number
                      </label>
                      <input
                        type="text"
                        value={companyData.registration}
                        onChange={(e) =>
                          setCompanyData({ ...companyData, registration: e.target.value })
                        }
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-bold text-gray-600 uppercase mb-2 block">
                        VAT Number
                      </label>
                      <input
                        type="text"
                        value={companyData.vat}
                        onChange={(e) =>
                          setCompanyData({ ...companyData, vat: e.target.value })
                        }
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-bold text-gray-600 uppercase mb-2 block">
                      Industry
                    </label>
                    <select 
                      value={companyData.industry}
                      onChange={(e) =>
                        setCompanyData({ ...companyData, industry: e.target.value })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
                    >
                      <option value="">Select an industry</option>
                      <option value="Real Estate Management">Real Estate Management</option>
                      <option value="Property Management">Property Management</option>
                      <option value="Facilities Management">Facilities Management</option>
                      <option value="Hospitality">Hospitality</option>
                      <option value="Retail">Retail</option>
                    </select>
                  </div>

                  <Button 
                    onClick={handleSaveCompanyData}
                    disabled={saving}
                    className="bg-green-700 hover:bg-green-800 text-white"
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Security */}
            <Card className="border-1">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Security</h2>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div>
                      <p className="font-semibold text-gray-900">Password</p>
                      <p className="text-sm text-gray-600">
                        Update your account password regularly to stay secure
                      </p>
                    </div>
                    <Button 
                      onClick={handleChangePassword}
                      disabled={saving}
                      className="bg-green-700 hover:bg-green-800 text-white"
                    >
                      {saving ? 'Updating...' : 'Change Password'}
                    </Button>
                  </div>

                  <div className="p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-gray-900">Two-Factor Authentication</p>
                        <p className="text-sm text-gray-600">
                          Add an extra layer of security to your account
                        </p>
                      </div>
                      {!totpSetup && (
                        <Button
                          variant="outline"
                          className="text-green-700 border-green-700"
                          onClick={handleEnable2FA}
                          disabled={enabling2FA}
                        >
                          {enabling2FA ? 'Loading...' : 'Enable 2FA'}
                        </Button>
                      )}
                    </div>

                    {totpSetup && (
                      <div className="mt-4 pt-4 border-t border-gray-200 space-y-3">
                        <p className="text-sm text-gray-700">Scan this QR code with your authenticator app, then enter the 6-digit code below.</p>
                        <img src={totpSetup.qr_code} alt="2FA QR code" className="h-40 w-40 border border-gray-200 rounded" />
                        <p className="text-xs text-gray-500">Manual entry key: <span className="font-mono">{totpSetup.secret}</span></p>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={totpCode}
                            onChange={(e) => setTotpCode(e.target.value)}
                            placeholder="123456"
                            maxLength={6}
                            className="px-3 py-2 border border-gray-300 rounded-lg w-32"
                          />
                          <Button
                            onClick={handleVerify2FA}
                            disabled={saving || totpCode.length !== 6}
                            className="bg-green-700 hover:bg-green-800 text-white"
                          >
                            {saving ? 'Verifying...' : 'Verify & Enable'}
                          </Button>
                          <Button
                            variant="ghost"
                            onClick={() => { setTotpSetup(null); setTotpCode(''); }}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Account Status */}
            <Card className="border-1">
              <CardContent className="p-6">
                <h3 className="text-sm font-bold text-gray-600 uppercase mb-4">
                  Account Status
                </h3>

                {accountStatus ? (
                  <div className="space-y-3 mb-6">
                    <div>
                      <p className="text-xs text-gray-600 uppercase mb-1">Customer ID</p>
                      <p className="font-bold text-gray-900">{accountStatus.customerId}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600 uppercase mb-1">Status</p>
                      <span className="inline-block px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold">
                        ● {accountStatus.status}
                      </span>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600 uppercase mb-1">Member since</p>
                      <p className="font-semibold text-gray-900">{accountStatus.memberSince}</p>
                    </div>
                  </div>
                ) : (
                  <div className="text-gray-500 text-sm mb-6">Loading account status...</div>
                )}

                {accountStatus && (
                  <div className="grid grid-cols-2 gap-4 mb-6 pb-6 border-b border-gray-200">
                    <div>
                      <p className="text-sm font-bold text-gray-900">
                        {accountStatus.totalOrders}
                      </p>
                      <p className="text-xs text-gray-600 uppercase">Total Orders</p>
                    </div>
                    <div>
                      <p className="text-sm font-bold text-gray-900">
                        {accountStatus.totalSpend}
                      </p>
                      <p className="text-xs text-gray-600 uppercase">Total Spend</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Billing Details */}
            <Card className="border-1">
              <CardContent className="p-6">
                <h3 className="text-sm font-bold text-gray-600 uppercase mb-4">
                  Billing Details
                </h3>

                <div className="mb-6 pb-6 border-b border-gray-200">
                  {billingData ? (
                    <>
                      <p className="text-sm text-gray-900 font-semibold mb-1 capitalize">
                        {billingData.cardBrand} ending in {billingData.cardLast4}
                      </p>
                      <p className="text-xs text-gray-600">Expires {billingData.cardExpiry}</p>
                    </>
                  ) : (
                    <p className="text-sm text-gray-500">No payment method on file</p>
                  )}
                </div>

                {!showCardForm ? (
                  <Button
                    onClick={() => setShowCardForm(true)}
                    className="w-full border border-gray-300 text-gray-700 hover:bg-gray-50"
                  >
                    {billingData ? 'Update Payment Method' : 'Add Payment Method'}
                  </Button>
                ) : (
                  <form onSubmit={handleSaveCard} className="space-y-3">
                    <div className="p-3 border border-gray-300 rounded-lg">
                      <CardElement options={{ style: { base: { fontSize: '14px' } } }} />
                    </div>
                    {cardError && (
                      <p className="text-xs text-red-600">{cardError}</p>
                    )}
                    <div className="flex gap-2">
                      <Button
                        type="submit"
                        disabled={savingCard || !stripe}
                        className="flex-1 bg-green-700 hover:bg-green-800 text-white"
                      >
                        {savingCard ? 'Saving...' : 'Save Card'}
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => { setShowCardForm(false); setCardError(null); }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </form>
                )}
              </CardContent>
            </Card>

            {/* Refer & Earn */}
            <Card className="border-1 bg-green-700 text-white">
              <CardContent className="p-6">
                <h3 className="text-sm font-bold uppercase mb-2">💰 Refer & Earn</h3>
                <p className="text-sm mb-4">
                  Invite friends and earn £50 credit for every successful referral.
                </p>
                <div className="bg-white bg-opacity-20 rounded px-3 py-2 mb-4 font-mono text-sm">
                  {referralCode === 'UNAVAILABLE' ? 'Unavailable right now' : (referralCode || 'Loading...')}
                </div>
                {referralCredits !== null && referralCode !== 'UNAVAILABLE' && (
                  <p className="text-xs text-green-50 mb-4">
                    Credits earned so far: <span className="font-bold">£{referralCredits.toFixed(2)}</span>
                  </p>
                )}
                <Button
                  onClick={handleShareReferral}
                  disabled={!referralCode || referralCode === 'UNAVAILABLE' || saving}
                  className="w-full bg-white text-green-700 hover:bg-gray-100 font-semibold"
                >
                  {saving ? 'Copying...' : 'Share Referral Link'}
                </Button>
              </CardContent>
            </Card>

            {/* Account Management */}
            <Card className="border-1 bg-gray-50">
              <CardContent className="p-6">
                <h3 className="text-sm font-bold text-gray-900 uppercase mb-4">
                  Account Management
                </h3>
                <div className="space-y-2">
                  <p className="text-xs text-gray-600 mb-3">
                    ⚠️ Warning: Deactivating your account will pause all active services and remove access to your dashboard.
                  </p>
                  <button 
                    onClick={handleRequestDataExport}
                    disabled={saving}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 text-sm font-semibold"
                  >
                    <Download className="h-4 w-4" />
                    {saving ? 'Processing...' : 'Request Data Export'}
                  </button>
                  <button 
                    onClick={handleDeactivateAccount}
                    disabled={saving}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 text-sm font-semibold"
                  >
                    <Trash2 className="h-4 w-4" />
                    {saving ? 'Processing...' : 'Deactivate Account'}
                  </button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AccountSettingsPage;
